import React, { useState, useEffect } from "react";
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  Alert, 
  Spinner,
  Modal
} from "react-bootstrap";
import { FaSave, FaUndo, FaTrash, FaToggleOn, FaToggleOff, FaPlus } from "react-icons/fa";

const TestimonialSectionAdmin = () => {
  const [testimonialData, setTestimonialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);

  useEffect(() => {
    fetchTestimonialData();
  }, []);

  const fetchTestimonialData = async () => {
    try {
      const response = await fetch("https://ecom-22-fe6311cae608.herokuapp.com/api/testimonial-section");
      const data = await response.json();
      
      if (data.success) {
        setTestimonialData(data.data);
      } else {
        setError(data.message || "Failed to fetch testimonial section data");
      }
    } catch (err) {
      setError("Network error. Please try again later.");
      console.error("Error fetching testimonial section:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTestimonialData({
      ...testimonialData,
      [name]: value
    });
  };

  const handleTestimonialChange = (index, field, value) => {
    const updatedTestimonials = [...testimonialData.testimonials];
    updatedTestimonials[index] = {
      ...updatedTestimonials[index],
      [field]: value
    };
    setTestimonialData({
      ...testimonialData,
      testimonials: updatedTestimonials
    });
  };

  const addTestimonial = () => {
    const newTestimonial = {
      id: Date.now(),
      text: "New testimonial text",
      author: "New Author",
      position: "Position",
      rating: 5
    };
    setTestimonialData({
      ...testimonialData,
      testimonials: [...testimonialData.testimonials, newTestimonial]
    });
  };

  const removeTestimonial = (index) => {
    const updatedTestimonials = [...testimonialData.testimonials];
    updatedTestimonials.splice(index, 1);
    setTestimonialData({
      ...testimonialData,
      testimonials: updatedTestimonials
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch("https://ecom-22-fe6311cae608.herokuapp.com/api/testimonial-section", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(testimonialData)
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess("Testimonial section updated successfully!");
        setTestimonialData(data.data);
      } else {
        setError(data.message || "Failed to update testimonial section");
      }
    } catch (err) {
      setError("Network error. Please try again later.");
      console.error("Error updating testimonial section:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    try {
      const response = await fetch("https://ecom-22-fe6311cae608.herokuapp.com/api/testimonial-section/toggle", {
        method: "PATCH"
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess(`Testimonial section ${data.data.isActive ? "activated" : "deactivated"} successfully!`);
        setTestimonialData(data.data);
      } else {
        setError(data.message || "Failed to toggle testimonial section status");
      }
    } catch (err) {
      setError("Network error. Please try again later.");
      console.error("Error toggling testimonial section:", err);
    }
  };

  const handleReset = async () => {
    try {
      const response = await fetch("https://ecom-22-fe6311cae608.herokuapp.com/api/testimonial-section/reset", {
        method: "POST"
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess("Testimonial section reset to default successfully!");
        setTestimonialData(data.data);
        setShowResetModal(false);
      } else {
        setError(data.message || "Failed to reset testimonial section");
      }
    } catch (err) {
      setError("Network error. Please try again later.");
      console.error("Error resetting testimonial section:", err);
    }
  };

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden"></span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!testimonialData) {
    return (
      <Container className="py-5">
        <Alert variant="danger">Failed to load testimonial section data</Alert>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <h2 className="mb-4">Manage Testimonial Section</h2>
      
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Section Status</h5>
          <Button 
            variant={testimonialData.isActive ? "success" : "secondary"}
            onClick={handleToggleActive}
          >
            {testimonialData.isActive ? <FaToggleOn /> : <FaToggleOff />} 
            {testimonialData.isActive ? " Active" : " Inactive"}
          </Button>
        </Card.Header>
        <Card.Body>
          <p>
            Last updated: {new Date(testimonialData.updatedAt).toLocaleString()}
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Section Content</h5>
        </Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              name="title"
              value={testimonialData.title}
              onChange={handleInputChange}
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Subtitle</Form.Label>
            <Form.Control
              type="text"
              name="subtitle"
              value={testimonialData.subtitle}
              onChange={handleInputChange}
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Image URL</Form.Label>
            <Form.Control
              type="text"
              name="imageUrl"
              value={testimonialData.imageUrl}
              onChange={handleInputChange}
              placeholder="https://example.com/image.jpg"
            />
            {testimonialData.imageUrl && (
              <div className="mt-2">
                <img 
                  src={testimonialData.imageUrl} 
                  alt="Preview" 
                  style={{ maxWidth: "200px", maxHeight: "200px" }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://picsum.photos/seed/error/200/200.jpg";
                  }}
                />
              </div>
            )}
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Satisfaction Percentage</Form.Label>
            <Form.Control
              type="number"
              name="satisfactionPercentage"
              value={testimonialData.satisfactionPercentage}
              onChange={handleInputChange}
              min="0"
              max="100"
            />
          </Form.Group>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Testimonials</h5>
          <Button variant="primary" onClick={addTestimonial}>
            <FaPlus /> Add Testimonial
          </Button>
        </Card.Header>
        <Card.Body>
          {testimonialData.testimonials.map((testimonial, index) => (
            <div key={testimonial.id || index} className="mb-4 border p-3 rounded">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6>Testimonial {index + 1}</h6>
                <Button 
                  variant="danger" 
                  size="sm"
                  onClick={() => removeTestimonial(index)}
                >
                  <FaTrash />
                </Button>
              </div>
              
              <Form.Group className="mb-3">
                <Form.Label>Text</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={testimonial.text}
                  onChange={(e) => handleTestimonialChange(index, "text", e.target.value)}
                />
              </Form.Group>
              
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Author</Form.Label>
                    <Form.Control
                      type="text"
                      value={testimonial.author}
                      onChange={(e) => handleTestimonialChange(index, "author", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Position</Form.Label>
                    <Form.Control
                      type="text"
                      value={testimonial.position}
                      onChange={(e) => handleTestimonialChange(index, "position", e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Rating</Form.Label>
                    <Form.Select
                      value={testimonial.rating}
                      onChange={(e) => handleTestimonialChange(index, "rating", parseInt(e.target.value))}
                    >
                      <option value={1}>1 Star</option>
                      <option value={2}>2 Stars</option>
                      <option value={3}>3 Stars</option>
                      <option value={4}>4 Stars</option>
                      <option value={5}>5 Stars</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </div>
          ))}
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-between">
        <Button variant="secondary" onClick={() => setShowResetModal(true)}>
          <FaUndo /> Reset to Default
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              <span className="visually-hidden"></span>
            </>
          ) : (
            <><FaSave /> Save Changes</>
          )}
        </Button>
      </div>

      <Modal show={showResetModal} onHide={() => setShowResetModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reset to Default</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to reset the testimonial section to default values? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResetModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleReset}>
            Reset
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default TestimonialSectionAdmin;